import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowLeft, RefreshCw, Plus, ChevronDown, ChevronUp, Search,
  Building, Clock, CheckCircle, XCircle, AlertTriangle, FileText,
  User, Mail, Phone, MapPin, Upload, Eye, Edit, Send, Ban
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { API_URL } from '../../utils/apiConfig';

const StatusBadge = ({ status }) => {
  const colors = {
    draft: 'bg-slate-600 text-slate-200',
    submitted: 'bg-blue-600 text-blue-100',
    under_review: 'bg-purple-600 text-purple-100',
    pending_documents: 'bg-amber-600 text-amber-100',
    pending_approval: 'bg-cyan-600 text-cyan-100',
    approved: 'bg-emerald-600 text-emerald-100',
    active: 'bg-green-500 text-white',
    rejected: 'bg-red-600 text-red-100',
    suspended: 'bg-orange-600 text-orange-100',
    closed: 'bg-slate-500 text-slate-200',
  };
  return (
    <span className={`px-2 py-1 rounded text-xs font-medium uppercase ${colors[status] || 'bg-slate-600'}`}>
      {status?.replace(/_/g, ' ')}
    </span>
  );
};

const ProgressBar = ({ progress }) => {
  const pct = progress?.overall_percent || 0;
  const color = pct >= 80 ? 'bg-emerald-500' : pct >= 50 ? 'bg-amber-500' : 'bg-red-500';
  
  return (
    <div className="w-full">
      <div className="flex justify-between text-xs text-slate-400 mb-1">
        <span>Progress</span>
        <span>{pct}%</span>
      </div>
      <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
        <div className={`h-full ${color} transition-all duration-500`} style={{ width: `${pct}%` }}></div>
      </div>
    </div>
  );
};

const ChecklistItem = ({ item, onUpdate }) => {
  const statusColors = {
    not_started: 'text-slate-500',
    in_progress: 'text-amber-400',
    completed: 'text-emerald-400',
    failed: 'text-red-400',
    skipped: 'text-slate-400',
  };
  
  const statusIcons = {
    not_started: <div className="w-4 h-4 rounded-full border-2 border-slate-500" />,
    in_progress: <Clock className="w-4 h-4 text-amber-400" />,
    completed: <CheckCircle className="w-4 h-4 text-emerald-400" />,
    failed: <XCircle className="w-4 h-4 text-red-400" />,
    skipped: <div className="w-4 h-4 text-slate-400">—</div>,
  };
  
  return (
    <div className="flex items-center justify-between py-2 border-b border-slate-700 last:border-0">
      <div className="flex items-center gap-3">
        {statusIcons[item.status]}
        <span className={`text-sm ${item.status === 'completed' ? 'text-white' : 'text-slate-400'}`}>
          {item.label}
          {item.required && <span className="text-red-400 ml-1">*</span>}
        </span>
      </div>
      <select
        value={item.status}
        onChange={(e) => onUpdate(item.key, e.target.value)}
        className="bg-slate-800 border border-slate-600 rounded px-2 py-1 text-xs text-white"
      >
        <option value="not_started">Not Started</option>
        <option value="in_progress">In Progress</option>
        <option value="completed">Completed</option>
        <option value="failed">Failed</option>
        <option value="skipped">Skipped</option>
      </select>
    </div>
  );
};

export default function AdminOnboardingView() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  const [applications, setApplications] = useState([]);
  const [selectedApp, setSelectedApp] = useState(null);
  const [activeTab, setActiveTab] = useState('dashboard');
  
  // Filters
  const [statusFilter, setStatusFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  
  // Modals
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [showDocumentModal, setShowDocumentModal] = useState(false);
  
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');

  // Create form
  const [createForm, setCreateForm] = useState({
    business_name: '',
    business_type: 'colmado',
    owner_name: '',
    email: '',
    phone: '',
    street_address: '',
    city: '',
    province: '',
    country: 'DO',
    tax_id: '',
    bank_name: '',
    bank_account_number: '',
    notes: ''
  });
  
  // Status transition
  const [statusTransition, setStatusTransition] = useState({
    new_status: '',
    notes: '',
    rejection_reason: ''
  });
  
  // Document form
  const [documentForm, setDocumentForm] = useState({
    document_type: 'business_license',
    file_reference: '',
    file_name: '',
    notes: ''
  });

  const getToken = () => localStorage.getItem('pecash_token');

  const fetchDashboard = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/onboarding/dashboard`, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      if (res.ok) {
        const data = await res.json();
        setDashboard(data);
      }
    } catch (err) {
      console.error('Dashboard fetch error:', err);
    }
  }, []);

  const fetchApplications = useCallback(async () => {
    try {
      let url = `${API_URL}/api/admin/onboarding/applications?limit=100`;
      if (statusFilter) url += `&status=${statusFilter}`;
      if (searchTerm) url += `&search=${encodeURIComponent(searchTerm)}`;
      
      const res = await fetch(url, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      if (res.ok) {
        const data = await res.json();
        setApplications(data.applications || []);
      }
    } catch (err) {
      console.error('Applications fetch error:', err);
    }
  }, [statusFilter, searchTerm]);

  const fetchApplicationDetail = async (appId) => {
    try {
      const res = await fetch(`${API_URL}/api/admin/onboarding/applications/${appId}`, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      if (res.ok) {
        const data = await res.json();
        setSelectedApp(data);
      }
    } catch (err) {
      console.error('Application detail fetch error:', err);
    }
  };

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      await Promise.all([fetchDashboard(), fetchApplications()]);
      setLoading(false);
    };
    load();
  }, [fetchDashboard, fetchApplications]);

  useEffect(() => {
    fetchApplications();
  }, [statusFilter, searchTerm, fetchApplications]);

  const handleCreateApplication = async () => {
    setError('');
    try {
      const res = await fetch(`${API_URL}/api/admin/onboarding/applications`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${getToken()}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(createForm)
      });
      
      if (res.ok) {
        const data = await res.json();
        setShowCreateModal(false);
        setCreateForm({
          business_name: '', business_type: 'colmado', owner_name: '', email: '', phone: '',
          street_address: '', city: '', province: '', country: 'DO', tax_id: '',
          bank_name: '', bank_account_number: '', notes: ''
        });
        setSuccessMsg(`Application ${data.application_number} created`);
        await Promise.all([fetchDashboard(), fetchApplications()]);
        setTimeout(() => setSuccessMsg(''), 3000);
      } else {
        const err = await res.json();
        setError(err.detail || 'Failed to create application');
      }
    } catch (err) {
      setError('Network error');
    }
  };

  const handleStatusTransition = async () => {
    if (!selectedApp || !statusTransition.new_status) return;
    setError('');
    
    try {
      const res = await fetch(`${API_URL}/api/admin/onboarding/applications/${selectedApp.application.id}/status`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${getToken()}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(statusTransition)
      });
      
      if (res.ok) {
        const data = await res.json();
        setShowStatusModal(false);
        setStatusTransition({ new_status: '', notes: '', rejection_reason: '' });
        setSuccessMsg(data.message);
        await Promise.all([fetchDashboard(), fetchApplications()]);
        await fetchApplicationDetail(selectedApp.application.id);
        setTimeout(() => setSuccessMsg(''), 3000);
      } else {
        const err = await res.json();
        setError(err.detail || 'Status transition failed');
      }
    } catch (err) {
      setError('Network error');
    }
  };

  const handleAddDocument = async () => {
    if (!selectedApp) return;
    setError('');
    
    try {
      const res = await fetch(`${API_URL}/api/admin/onboarding/applications/${selectedApp.application.id}/documents`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${getToken()}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(documentForm)
      });
      
      if (res.ok) {
        setShowDocumentModal(false);
        setDocumentForm({ document_type: 'business_license', file_reference: '', file_name: '', notes: '' });
        setSuccessMsg('Document added');
        await fetchApplicationDetail(selectedApp.application.id);
        setTimeout(() => setSuccessMsg(''), 3000);
      } else {
        const err = await res.json();
        setError(err.detail || 'Failed to add document');
      }
    } catch (err) {
      setError('Network error');
    }
  };

  const handleReviewDocument = async (docId, status) => {
    if (!selectedApp) return;
    
    try {
      const res = await fetch(`${API_URL}/api/admin/onboarding/applications/${selectedApp.application.id}/documents/${docId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${getToken()}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status })
      });
      
      if (res.ok) {
        setSuccessMsg(`Document marked as ${status}`);
        await fetchApplicationDetail(selectedApp.application.id);
        setTimeout(() => setSuccessMsg(''), 3000);
      }
    } catch (err) {
      setError('Network error');
    }
  };

  const handleChecklistUpdate = async (itemKey, status) => {
    if (!selectedApp) return;
    
    try {
      const res = await fetch(`${API_URL}/api/admin/onboarding/applications/${selectedApp.application.id}/checklist`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${getToken()}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ item_key: itemKey, status })
      });
      
      if (res.ok) {
        await fetchApplicationDetail(selectedApp.application.id);
      }
    } catch (err) {
      setError('Network error');
    }
  };

  // Get allowed transitions for current status
  const getAllowedTransitions = (currentStatus) => {
    const transitions = {
      draft: ['submitted'],
      submitted: ['under_review', 'rejected'],
      under_review: ['pending_documents', 'pending_approval', 'rejected'],
      pending_documents: ['under_review', 'rejected'],
      pending_approval: ['approved', 'rejected', 'under_review'],
      approved: ['active'],
      active: ['suspended'],
      rejected: ['draft'],
      suspended: ['active', 'closed'],
      closed: []
    };
    return transitions[currentStatus] || [];
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <RefreshCw className="w-8 h-8 text-emerald-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900">
      {/* Header */}
      <header className="bg-slate-800 border-b border-slate-700 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              onClick={() => navigate('/admin/dashboard')}
              className="text-slate-400 hover:text-white"
            >
              <ArrowLeft className="w-5 h-5 mr-2" /> Back
            </Button>
            <div>
              <h1 className="text-xl font-bold text-white flex items-center gap-2">
                <Building className="w-6 h-6 text-emerald-400" />
                Merchant Onboarding
              </h1>
              <p className="text-sm text-slate-400">Application review & activation</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {dashboard?.statistics?.pending_review > 0 && (
              <span className="px-3 py-1 bg-purple-900/50 border border-purple-700 rounded-full text-purple-300 text-xs font-medium">
                {dashboard.statistics.pending_review} PENDING REVIEW
              </span>
            )}
            <Button 
              onClick={() => setShowCreateModal(true)}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="onboarding-create-application"
            >
              <Plus className="w-4 h-4 mr-2" /> New Application
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Messages */}
        {successMsg && (
          <div className="bg-emerald-900/30 border border-emerald-700 rounded-xl p-4 mb-6">
            <p className="text-emerald-400 flex items-center gap-2">
              <CheckCircle className="w-4 h-4" /> {successMsg}
            </p>
          </div>
        )}
        {error && (
          <div className="bg-red-900/30 border border-red-700 rounded-xl p-4 mb-6">
            <p className="text-red-400 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4" /> {error}
            </p>
            <Button size="sm" variant="ghost" className="mt-2 text-red-400" onClick={() => setError('')}>
              Dismiss
            </Button>
          </div>
        )}

        {/* Stats Cards */}
        {dashboard && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-slate-800 border border-blue-700/50 rounded-xl p-4"
            >
              <p className="text-blue-400 text-xs mb-1">Submitted</p>
              <p className="text-3xl font-bold text-white">{dashboard.statistics?.by_status?.submitted || 0}</p>
            </motion.div>
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-slate-800 border border-purple-700/50 rounded-xl p-4"
            >
              <p className="text-purple-400 text-xs mb-1">Under Review</p>
              <p className="text-3xl font-bold text-white">{dashboard.statistics?.by_status?.under_review || 0}</p>
            </motion.div>
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-slate-800 border border-cyan-700/50 rounded-xl p-4"
            >
              <p className="text-cyan-400 text-xs mb-1">Pending Approval</p>
              <p className="text-3xl font-bold text-white">{dashboard.statistics?.by_status?.pending_approval || 0}</p>
            </motion.div>
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="bg-slate-800 border border-emerald-700/50 rounded-xl p-4"
            >
              <p className="text-emerald-400 text-xs mb-1">Active Merchants</p>
              <p className="text-3xl font-bold text-white">{dashboard.statistics?.active_merchants || 0}</p>
            </motion.div>
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="bg-slate-800 border border-green-700/50 rounded-xl p-4"
            >
              <p className="text-green-400 text-xs mb-1">Activated (Week)</p>
              <p className="text-3xl font-bold text-white">{dashboard.statistics?.activated_this_week || 0}</p>
            </motion.div>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-2 mb-6 border-b border-slate-700 pb-4">
          <Button
            data-testid="onboarding-tab-dashboard"
            variant={activeTab === 'dashboard' ? 'default' : 'ghost'}
            className={activeTab === 'dashboard' ? 'bg-emerald-600' : 'text-slate-400'}
            onClick={() => { setActiveTab('dashboard'); setSelectedApp(null); }}
          >
            <FileText className="w-4 h-4 mr-1" /> Dashboard
          </Button>
          <Button
            data-testid="onboarding-tab-applications"
            variant={activeTab === 'applications' ? 'default' : 'ghost'}
            className={activeTab === 'applications' ? 'bg-emerald-600' : 'text-slate-400'}
            onClick={() => { setActiveTab('applications'); setSelectedApp(null); }}
          >
            <Building className="w-4 h-4 mr-1" /> Applications ({applications.length})
          </Button>
          {selectedApp && (
            <Button
              data-testid="onboarding-tab-detail"
              variant="default"
              className="bg-emerald-600"
            >
              <Eye className="w-4 h-4 mr-1" /> {selectedApp.application.application_number}
            </Button>
          )}
        </div>

        {/* Dashboard Tab */}
        {activeTab === 'dashboard' && !selectedApp && dashboard && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Pending Review */}
            <div className="bg-slate-800 border border-slate-700 rounded-xl p-6">
              <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                <Clock className="w-5 h-5 text-purple-400" />
                Pending Review ({dashboard.pending_review?.length || 0})
              </h3>
              {dashboard.pending_review?.length > 0 ? (
                <div className="space-y-2">
                  {dashboard.pending_review.map((app) => (
                    <div 
                      key={app.id} 
                      className="bg-slate-900 rounded-lg p-3 cursor-pointer hover:bg-slate-700/50"
                      onClick={() => { fetchApplicationDetail(app.id); setActiveTab('detail'); }}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-white text-sm font-medium">{app.business_name}</p>
                          <p className="text-slate-400 text-xs">{app.application_number}</p>
                        </div>
                        <StatusBadge status={app.status} />
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-slate-500 text-center py-4">No applications pending review</p>
              )}
            </div>

            {/* Pending Approval */}
            <div className="bg-slate-800 border border-slate-700 rounded-xl p-6">
              <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-cyan-400" />
                Pending Approval ({dashboard.pending_approval?.length || 0})
              </h3>
              {dashboard.pending_approval?.length > 0 ? (
                <div className="space-y-2">
                  {dashboard.pending_approval.map((app) => (
                    <div 
                      key={app.id} 
                      className="bg-slate-900 rounded-lg p-3 cursor-pointer hover:bg-slate-700/50"
                      onClick={() => { fetchApplicationDetail(app.id); setActiveTab('detail'); }}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-white text-sm font-medium">{app.business_name}</p>
                          <p className="text-slate-400 text-xs">{app.application_number} • Reviewed by {app.reviewer}</p>
                        </div>
                        <StatusBadge status={app.status} />
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-slate-500 text-center py-4">No applications pending approval</p>
              )}
            </div>

            {/* Recent Applications */}
            <div className="bg-slate-800 border border-slate-700 rounded-xl p-6 lg:col-span-2">
              <h3 className="text-white font-semibold mb-4">Recent Applications</h3>
              {dashboard.recent_applications?.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="border-b border-slate-700">
                      <tr>
                        <th className="text-left text-slate-400 text-xs px-3 py-2">Application</th>
                        <th className="text-left text-slate-400 text-xs px-3 py-2">Business</th>
                        <th className="text-left text-slate-400 text-xs px-3 py-2">Status</th>
                        <th className="text-left text-slate-400 text-xs px-3 py-2">Progress</th>
                        <th className="text-left text-slate-400 text-xs px-3 py-2">Assigned</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-700">
                      {dashboard.recent_applications.map((app) => (
                        <tr 
                          key={app.id} 
                          className="hover:bg-slate-700/50 cursor-pointer"
                          onClick={() => { fetchApplicationDetail(app.id); setActiveTab('detail'); }}
                        >
                          <td className="px-3 py-2 text-emerald-400 text-sm">{app.application_number}</td>
                          <td className="px-3 py-2 text-white text-sm">{app.business_name}</td>
                          <td className="px-3 py-2"><StatusBadge status={app.status} /></td>
                          <td className="px-3 py-2 w-32">
                            <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
                              <div className="h-full bg-emerald-500" style={{ width: `${app.progress?.overall_percent || 0}%` }}></div>
                            </div>
                          </td>
                          <td className="px-3 py-2 text-slate-400 text-xs">{app.assigned_to || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-slate-500 text-center py-4">No applications yet</p>
              )}
            </div>
          </div>
        )}

        {/* Applications Tab */}
        {activeTab === 'applications' && !selectedApp && (
          <div className="space-y-4">
            {/* Filters */}
            <div className="flex flex-wrap gap-4 mb-4">
              <div className="relative flex-1 min-w-[200px]">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                <input
                  type="text"
                  placeholder="Search applications..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full bg-slate-800 border border-slate-600 rounded-lg pl-10 pr-4 py-2 text-white text-sm"
                />
              </div>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="bg-slate-800 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm"
              >
                <option value="">All Statuses</option>
                <option value="draft">Draft</option>
                <option value="submitted">Submitted</option>
                <option value="under_review">Under Review</option>
                <option value="pending_documents">Pending Documents</option>
                <option value="pending_approval">Pending Approval</option>
                <option value="approved">Approved</option>
                <option value="active">Active</option>
                <option value="rejected">Rejected</option>
                <option value="suspended">Suspended</option>
              </select>
            </div>

            {/* Applications Table */}
            <div className="bg-slate-800 border border-slate-700 rounded-xl overflow-hidden">
              <table className="w-full">
                <thead className="bg-slate-900 border-b border-slate-700">
                  <tr>
                    <th className="text-left text-slate-400 text-xs font-medium px-4 py-3">Application</th>
                    <th className="text-left text-slate-400 text-xs font-medium px-4 py-3">Business</th>
                    <th className="text-left text-slate-400 text-xs font-medium px-4 py-3">Contact</th>
                    <th className="text-left text-slate-400 text-xs font-medium px-4 py-3">Status</th>
                    <th className="text-left text-slate-400 text-xs font-medium px-4 py-3">Progress</th>
                    <th className="text-left text-slate-400 text-xs font-medium px-4 py-3">Created</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-700">
                  {applications.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="text-center text-slate-500 py-8">
                        No applications found
                      </td>
                    </tr>
                  ) : (
                    applications.map((app) => (
                      <tr 
                        key={app.id}
                        className="hover:bg-slate-700/50 cursor-pointer"
                        onClick={() => { fetchApplicationDetail(app.id); setActiveTab('detail'); }}
                      >
                        <td className="px-4 py-3">
                          <p className="text-emerald-400 text-sm font-medium">{app.application_number}</p>
                        </td>
                        <td className="px-4 py-3">
                          <p className="text-white text-sm">{app.business_name}</p>
                          <p className="text-slate-400 text-xs">{app.business_type}</p>
                        </td>
                        <td className="px-4 py-3">
                          <p className="text-slate-400 text-sm">{app.email}</p>
                          <p className="text-slate-500 text-xs">{app.phone}</p>
                        </td>
                        <td className="px-4 py-3"><StatusBadge status={app.status} /></td>
                        <td className="px-4 py-3 w-32">
                          <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
                            <div className="h-full bg-emerald-500" style={{ width: `${app.progress?.overall_percent || 0}%` }}></div>
                          </div>
                          <p className="text-slate-500 text-xs mt-1">{app.progress?.overall_percent || 0}%</p>
                        </td>
                        <td className="px-4 py-3 text-slate-400 text-xs">{new Date(app.created_at).toLocaleDateString()}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Application Detail View */}
        {selectedApp && (
          <div className="space-y-6">
            {/* Header Card */}
            <div className="bg-slate-800 border border-slate-700 rounded-xl p-6">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <div className="flex items-center gap-3 mb-2">
                    <h2 className="text-xl font-bold text-white">{selectedApp.application.application_number}</h2>
                    <StatusBadge status={selectedApp.application.status} />
                  </div>
                  <h3 className="text-lg text-white">{selectedApp.application.business_name}</h3>
                  <p className="text-slate-400 text-sm">{selectedApp.application.business_type} • {selectedApp.application.city}, {selectedApp.application.province}</p>
                </div>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="ghost"
                    className="text-slate-400"
                    onClick={() => setShowDocumentModal(true)}
                  >
                    <Upload className="w-4 h-4 mr-1" /> Add Document
                  </Button>
                  {getAllowedTransitions(selectedApp.application.status).length > 0 && (
                    <Button
                      size="sm"
                      className="bg-emerald-600 hover:bg-emerald-700"
                      onClick={() => setShowStatusModal(true)}
                    >
                      <Send className="w-4 h-4 mr-1" /> Change Status
                    </Button>
                  )}
                  <Button
                    size="sm"
                    variant="ghost"
                    className="text-slate-400"
                    onClick={() => { setSelectedApp(null); setActiveTab('applications'); }}
                  >
                    <XCircle className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              
              {/* Progress */}
              <div className="mb-6">
                <ProgressBar progress={selectedApp.application.progress} />
              </div>
              
              {/* Activation Status */}
              {selectedApp.application.status === 'approved' && (
                <div className={`rounded-lg p-4 mb-4 ${selectedApp.application.can_be_activated ? 'bg-emerald-900/30 border border-emerald-700' : 'bg-amber-900/30 border border-amber-700'}`}>
                  {selectedApp.application.can_be_activated ? (
                    <p className="text-emerald-400 text-sm flex items-center gap-2">
                      <CheckCircle className="w-4 h-4" /> Ready for activation
                    </p>
                  ) : (
                    <div>
                      <p className="text-amber-400 text-sm font-medium mb-2">Cannot activate yet:</p>
                      <ul className="text-amber-300 text-xs list-disc list-inside">
                        {selectedApp.application.activation_issues?.map((issue, i) => (
                          <li key={i}>{issue}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}

              {/* Business Info Grid */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <p className="text-slate-500 text-xs flex items-center gap-1"><User className="w-3 h-3" /> Owner</p>
                  <p className="text-white text-sm">{selectedApp.application.owner_name}</p>
                </div>
                <div>
                  <p className="text-slate-500 text-xs flex items-center gap-1"><Mail className="w-3 h-3" /> Email</p>
                  <p className="text-white text-sm">{selectedApp.application.email}</p>
                </div>
                <div>
                  <p className="text-slate-500 text-xs flex items-center gap-1"><Phone className="w-3 h-3" /> Phone</p>
                  <p className="text-white text-sm">{selectedApp.application.phone}</p>
                </div>
                <div>
                  <p className="text-slate-500 text-xs flex items-center gap-1"><MapPin className="w-3 h-3" /> Address</p>
                  <p className="text-white text-sm">{selectedApp.application.street_address}</p>
                </div>
              </div>
            </div>

            {/* Two Column Layout */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Checklist */}
              <div className="bg-slate-800 border border-slate-700 rounded-xl p-6">
                <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-emerald-400" />
                  Onboarding Checklist
                </h3>
                <div className="space-y-1">
                  {selectedApp.application.checklist?.map((item) => (
                    <ChecklistItem 
                      key={item.key} 
                      item={item} 
                      onUpdate={handleChecklistUpdate}
                    />
                  ))}
                </div>
              </div>

              {/* Documents */}
              <div className="bg-slate-800 border border-slate-700 rounded-xl p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-white font-semibold flex items-center gap-2">
                    <FileText className="w-5 h-5 text-blue-400" />
                    Documents ({selectedApp.application.documents?.length || 0})
                  </h3>
                  <Button size="sm" variant="ghost" className="text-emerald-400" onClick={() => setShowDocumentModal(true)}>
                    <Plus className="w-4 h-4 mr-1" /> Add
                  </Button>
                </div>
                {selectedApp.application.documents?.length > 0 ? (
                  <div className="space-y-2">
                    {selectedApp.application.documents.map((doc) => (
                      <div key={doc.id} className="bg-slate-900 rounded-lg p-3">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-white text-sm">{doc.file_name}</p>
                            <p className="text-slate-400 text-xs">{doc.document_type?.replace(/_/g, ' ')}</p>
                          </div>
                          <div className="flex items-center gap-2">
                            <StatusBadge status={doc.status} />
                            {doc.status === 'pending' && (
                              <div className="flex gap-1">
                                <Button size="sm" variant="ghost" className="text-emerald-400 px-2" onClick={() => handleReviewDocument(doc.id, 'verified')}>
                                  <CheckCircle className="w-4 h-4" />
                                </Button>
                                <Button size="sm" variant="ghost" className="text-red-400 px-2" onClick={() => handleReviewDocument(doc.id, 'rejected')}>
                                  <XCircle className="w-4 h-4" />
                                </Button>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-slate-500 text-center py-4">No documents uploaded</p>
                )}
              </div>
            </div>

            {/* History */}
            <div className="bg-slate-800 border border-slate-700 rounded-xl p-6">
              <h3 className="text-white font-semibold mb-4">Activity History</h3>
              {selectedApp.history?.length > 0 ? (
                <div className="space-y-2">
                  {selectedApp.history.map((h) => (
                    <div key={h.id} className="flex items-center gap-3 py-2 border-b border-slate-700 last:border-0">
                      <div className="w-2 h-2 rounded-full bg-emerald-400"></div>
                      <div className="flex-1">
                        <p className="text-white text-sm capitalize">{h.action.replace(/_/g, ' ')}</p>
                        {h.details && Object.keys(h.details).length > 0 && (
                          <p className="text-slate-500 text-xs">
                            {Object.entries(h.details).map(([k, v]) => `${k}: ${v}`).join(' • ')}
                          </p>
                        )}
                      </div>
                      <div className="text-right">
                        <p className="text-slate-400 text-xs">{h.performed_by}</p>
                        <p className="text-slate-500 text-xs">{new Date(h.timestamp).toLocaleString()}</p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-slate-500 text-center py-4">No history</p>
              )}
            </div>
          </div>
        )}

        {/* Create Modal */}
        {showCreateModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-slate-800 border border-slate-700 rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                  <Plus className="w-5 h-5 text-emerald-400" />
                  New Merchant Application
                </h3>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2 md:col-span-1">
                    <label className="block text-slate-400 text-sm mb-2">Business Name *</label>
                    <input
                      type="text"
                      value={createForm.business_name}
                      onChange={(e) => setCreateForm(prev => ({ ...prev, business_name: e.target.value }))}
                      className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white"
                      placeholder="Colmado El Sol"
                    />
                  </div>
                  <div>
                    <label className="block text-slate-400 text-sm mb-2">Business Type</label>
                    <select
                      value={createForm.business_type}
                      onChange={(e) => setCreateForm(prev => ({ ...prev, business_type: e.target.value }))}
                      className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white"
                    >
                      <option value="colmado">Colmado</option>
                      <option value="grocery">Grocery</option>
                      <option value="pharmacy">Pharmacy</option>
                      <option value="restaurant">Restaurant</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-slate-400 text-sm mb-2">Owner Name *</label>
                    <input
                      type="text"
                      value={createForm.owner_name}
                      onChange={(e) => setCreateForm(prev => ({ ...prev, owner_name: e.target.value }))}
                      className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-slate-400 text-sm mb-2">Email *</label>
                    <input
                      type="email"
                      value={createForm.email}
                      onChange={(e) => setCreateForm(prev => ({ ...prev, email: e.target.value }))}
                      className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-slate-400 text-sm mb-2">Phone *</label>
                    <input
                      type="text"
                      value={createForm.phone}
                      onChange={(e) => setCreateForm(prev => ({ ...prev, phone: e.target.value }))}
                      className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white"
                    />
                  </div>
                  <div className="col-span-2">
                    <label className="block text-slate-400 text-sm mb-2">Street Address *</label>
                    <input
                      type="text"
                      value={createForm.street_address}
                      onChange={(e) => setCreateForm(prev => ({ ...prev, street_address: e.target.value }))}
                      className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-slate-400 text-sm mb-2">City *</label>
                    <input
                      type="text"
                      value={createForm.city}
                      onChange={(e) => setCreateForm(prev => ({ ...prev, city: e.target.value }))}
                      className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-slate-400 text-sm mb-2">Province *</label>
                    <input
                      type="text"
                      value={createForm.province}
                      onChange={(e) => setCreateForm(prev => ({ ...prev, province: e.target.value }))}
                      className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white"
                    />
                  </div>
                </div>

                <div className="flex justify-end gap-3 mt-6 pt-6 border-t border-slate-700">
                  <Button variant="ghost" onClick={() => setShowCreateModal(false)}>Cancel</Button>
                  <Button
                    onClick={handleCreateApplication}
                    disabled={!createForm.business_name || !createForm.owner_name || !createForm.email || !createForm.phone || !createForm.street_address || !createForm.city || !createForm.province}
                    className="bg-emerald-600 hover:bg-emerald-700"
                  >
                    Create Application
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Status Transition Modal */}
        {showStatusModal && selectedApp && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-slate-800 border border-slate-700 rounded-xl max-w-md w-full">
              <div className="p-6">
                <h3 className="text-white font-semibold mb-4">Change Application Status</h3>
                <p className="text-slate-400 text-sm mb-4">Current: <StatusBadge status={selectedApp.application.status} /></p>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-slate-400 text-sm mb-2">New Status *</label>
                    <select
                      value={statusTransition.new_status}
                      onChange={(e) => setStatusTransition(prev => ({ ...prev, new_status: e.target.value }))}
                      className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white"
                    >
                      <option value="">Select status...</option>
                      {getAllowedTransitions(selectedApp.application.status).map(s => (
                        <option key={s} value={s}>{s.replace(/_/g, ' ').toUpperCase()}</option>
                      ))}
                    </select>
                  </div>
                  
                  {statusTransition.new_status === 'rejected' && (
                    <div>
                      <label className="block text-slate-400 text-sm mb-2">Rejection Reason *</label>
                      <textarea
                        value={statusTransition.rejection_reason}
                        onChange={(e) => setStatusTransition(prev => ({ ...prev, rejection_reason: e.target.value }))}
                        className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white h-24"
                        placeholder="Explain why the application is being rejected..."
                      />
                    </div>
                  )}
                  
                  <div>
                    <label className="block text-slate-400 text-sm mb-2">Notes</label>
                    <textarea
                      value={statusTransition.notes}
                      onChange={(e) => setStatusTransition(prev => ({ ...prev, notes: e.target.value }))}
                      className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white h-20"
                      placeholder="Optional notes..."
                    />
                  </div>
                </div>

                <div className="flex justify-end gap-3 mt-6 pt-6 border-t border-slate-700">
                  <Button variant="ghost" onClick={() => setShowStatusModal(false)}>Cancel</Button>
                  <Button
                    onClick={handleStatusTransition}
                    disabled={!statusTransition.new_status || (statusTransition.new_status === 'rejected' && !statusTransition.rejection_reason)}
                    className="bg-emerald-600 hover:bg-emerald-700"
                  >
                    Confirm
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Document Upload Modal */}
        {showDocumentModal && selectedApp && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-slate-800 border border-slate-700 rounded-xl max-w-md w-full">
              <div className="p-6">
                <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                  <Upload className="w-5 h-5 text-emerald-400" />
                  Add Document
                </h3>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-slate-400 text-sm mb-2">Document Type</label>
                    <select
                      value={documentForm.document_type}
                      onChange={(e) => setDocumentForm(prev => ({ ...prev, document_type: e.target.value }))}
                      className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white"
                    >
                      <option value="business_license">Business License</option>
                      <option value="tax_id">Tax ID</option>
                      <option value="owner_id">Owner ID</option>
                      <option value="proof_of_address">Proof of Address</option>
                      <option value="bank_statement">Bank Statement</option>
                      <option value="photo_storefront">Photo - Storefront</option>
                      <option value="photo_interior">Photo - Interior</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-slate-400 text-sm mb-2">File Name *</label>
                    <input
                      type="text"
                      value={documentForm.file_name}
                      onChange={(e) => setDocumentForm(prev => ({ ...prev, file_name: e.target.value }))}
                      className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white"
                      placeholder="business_license.pdf"
                    />
                  </div>
                  <div>
                    <label className="block text-slate-400 text-sm mb-2">File Reference (URL/ID) *</label>
                    <input
                      type="text"
                      value={documentForm.file_reference}
                      onChange={(e) => setDocumentForm(prev => ({ ...prev, file_reference: e.target.value }))}
                      className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white"
                      placeholder="s3://bucket/path/to/file.pdf"
                    />
                  </div>
                  <div>
                    <label className="block text-slate-400 text-sm mb-2">Notes</label>
                    <textarea
                      value={documentForm.notes}
                      onChange={(e) => setDocumentForm(prev => ({ ...prev, notes: e.target.value }))}
                      className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white h-20"
                    />
                  </div>
                </div>

                <div className="flex justify-end gap-3 mt-6 pt-6 border-t border-slate-700">
                  <Button variant="ghost" onClick={() => setShowDocumentModal(false)}>Cancel</Button>
                  <Button
                    onClick={handleAddDocument}
                    disabled={!documentForm.file_name || !documentForm.file_reference}
                    className="bg-emerald-600 hover:bg-emerald-700"
                  >
                    Add Document
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
